import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaTrophy, FaChevronRight, FaRedo, FaExclamationTriangle, FaTimes } from "react-icons/fa";
import { LuClock, LuCheck } from "react-icons/lu";
import { cn, destructiveOutlineBtnClass } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeletons";
import { HomePhaseShell } from "@/components/v2/home/HomePhaseShell";
import { HomeSecondarySection } from "@/components/v2/home/HomeSecondarySection";

export type PostMatchMode = "pending" | "edit" | "display" | "completed_recap";

interface HomePostMatchCardProps {
  mode: PostMatchMode;
  isAdmin: boolean;
  scoreA: string;
  scoreB: string;
  winner: "A" | "B" | "Draw" | null;
  onWinnerChange: (value: "A" | "B" | "Draw" | null) => void;
  displayScoreA?: number | null;
  displayScoreB?: number | null;
  winnerLabel?: string;
  recapDateLabel?: string;
  onScoreAChange: (value: string) => void;
  onScoreBChange: (value: string) => void;
  onLogResult: () => void;
  hasExistingResult?: boolean;
  savingResult?: boolean;
  canDispute?: boolean;
  onDispute?: () => void;
  disputing?: boolean;
  showStaleBanner?: boolean;
  onSkipStale?: () => void;
  skipped?: boolean;
  teaserLines?: string[];
  showLoopReset?: boolean;
  onStartNextWeek?: () => void;
  startingNextWeek?: boolean;
  onCancelMatch?: () => void;
  cancellingMatch?: boolean;
  historySlot?: ReactNode;
  commentsSlot?: ReactNode;
  teamsSlot?: ReactNode;
}

export function MatchResultScoreboard({
  mode,
  lightInput,
  darkInput,
  light,
  dark,
  onLightInputChange,
  onDarkInputChange,
  teamALabel,
  teamBLabel,
}: {
  mode: "pending" | "edit" | "display";
  lightInput?: string;
  darkInput?: string;
  light?: number | null;
  dark?: number | null;
  onLightInputChange?: (v: string) => void;
  onDarkInputChange?: (v: string) => void;
  teamALabel: string;
  teamBLabel: string;
}) {
  const scoreBoxClass =
    "flex size-24 items-center justify-center rounded-xl border-2 border-soccer-primary/30 bg-background/60 shadow-sm focus-within:border-soccer-primary/55";
  const scoreTextClass = "text-6xl font-bold tabular-nums leading-none";

  const renderScore = (side: "light" | "dark") => {
    const label = side === "light" ? teamALabel : teamBLabel;
    const value = side === "light" ? light : dark;
    const input = side === "light" ? lightInput : darkInput;
    const onInputChange = side === "light" ? onLightInputChange : onDarkInputChange;

    return (
      <div className="flex flex-col items-center">
        <div className={scoreBoxClass}>
          {mode === "pending" && <Skeleton className="size-12 rounded-md" />}
          {mode === "display" && (
            <p className={scoreTextClass}>{value != null ? value : "–"}</p>
          )}
          {mode === "edit" && (
            <input
              inputMode="numeric"
              value={input}
              onChange={(e) =>
                onInputChange?.(e.target.value.replace(/\D/g, "").slice(0, 2))
              }
              placeholder="–"
              aria-label={label}
              className={cn(
                scoreTextClass,
                "w-full border-0 bg-transparent text-center outline-none placeholder:text-muted-foreground/35"
              )}
            />
          )}
        </div>
        <p className="mt-2 text-sm font-semibold text-muted-foreground">{label}</p>
      </div>
    );
  };

  return (
    <div className={cn("text-center", mode === "pending" && "select-none")}>
      <div className="flex items-center justify-center gap-8">
        {renderScore("light")}
        <span
          className={cn(
            "text-3xl font-bold leading-none",
            mode === "pending" ? "text-muted-foreground/40" : "text-muted-foreground"
          )}
        >
          –
        </span>
        {renderScore("dark")}
      </div>
    </div>
  );
}

export function MatchResultAdminForm({
  scoreA,
  scoreB,
  winner,
  onScoreAChange,
  onScoreBChange,
  onWinnerChange,
  onSave,
  saveLabel,
  saving = false,
}: {
  scoreA: string;
  scoreB: string;
  winner: "A" | "B" | "Draw" | null;
  onScoreAChange: (value: string) => void;
  onScoreBChange: (value: string) => void;
  onWinnerChange: (value: "A" | "B" | "Draw" | null) => void;
  onSave: () => void;
  saveLabel: string;
  saving?: boolean;
}) {
  const { t } = useTranslation();
  const hasBothScores = scoreA !== "" && scoreB !== "";
  const hasWinner = winner === "A" || winner === "B" || winner === "Draw";
  const canSave = hasBothScores || hasWinner;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-semibold text-center">{t("v2.home.pickWinner")}</p>
        <RadioGroup
          value={winner ?? ""}
          onValueChange={(value) => onWinnerChange(value as "A" | "B" | "Draw")}
          className="grid grid-cols-1 gap-2 sm:grid-cols-3"
        >
          <div className="flex items-center gap-2 rounded-lg border px-3 py-2.5 has-[[data-state=checked]]:border-soccer-primary has-[[data-state=checked]]:bg-soccer-primary/5">
            <RadioGroupItem value="A" id="result-winner-a" />
            <Label htmlFor="result-winner-a" className="cursor-pointer font-semibold">
              {t("v2.home.wonTeamA")}
            </Label>
          </div>
          <div className="flex items-center gap-2 rounded-lg border px-3 py-2.5 has-[[data-state=checked]]:border-soccer-primary has-[[data-state=checked]]:bg-soccer-primary/5">
            <RadioGroupItem value="Draw" id="result-winner-draw" />
            <Label htmlFor="result-winner-draw" className="cursor-pointer font-semibold">
              {t("v2.home.matchDraw")}
            </Label>
          </div>
          <div className="flex items-center gap-2 rounded-lg border px-3 py-2.5 has-[[data-state=checked]]:border-soccer-primary has-[[data-state=checked]]:bg-soccer-primary/5">
            <RadioGroupItem value="B" id="result-winner-b" />
            <Label htmlFor="result-winner-b" className="cursor-pointer font-semibold">
              {t("v2.home.wonTeamB")}
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-2 border-t pt-4">
        <p className="text-xs text-center text-muted-foreground">{t("v2.home.optionalScore")}</p>
        <MatchResultScoreboard
          mode="edit"
          lightInput={scoreA}
          darkInput={scoreB}
          onLightInputChange={onScoreAChange}
          onDarkInputChange={onScoreBChange}
          teamALabel={t("v2.home.teamA")}
          teamBLabel={t("v2.home.teamB")}
        />
      </div>

      <Button
        className="w-full min-h-[48px] bg-amber-600 font-bold text-white hover:bg-amber-600/90 shadow-sm"
        disabled={!canSave || saving}
        onClick={onSave}
      >
        <LuCheck className="h-4 w-4" /> {saveLabel}
      </Button>
    </div>
  );
}

export function HomePostMatchCard({
  mode,
  isAdmin,
  scoreA,
  scoreB,
  winner,
  onWinnerChange,
  displayScoreA,
  displayScoreB,
  winnerLabel,
  recapDateLabel,
  onScoreAChange,
  onScoreBChange,
  onLogResult,
  hasExistingResult = false,
  savingResult = false,
  canDispute,
  onDispute,
  disputing,
  showStaleBanner,
  onSkipStale,
  skipped,
  teaserLines = [],
  showLoopReset,
  onStartNextWeek,
  startingNextWeek,
  onCancelMatch,
  cancellingMatch = false,
  historySlot,
  commentsSlot,
  teamsSlot,
}: HomePostMatchCardProps) {
  const { t } = useTranslation();

  if (skipped) {
    return (
      <HomePhaseShell
        eyebrow={t("v2.home.phaseEyebrowPostKickoff")}
        title={t("v2.home.staleSkippedTitle")}
        secondary={historySlot ? <div>{historySlot}</div> : undefined}
        adminSection={
          isAdmin && (onStartNextWeek || onCancelMatch) ? (
            <div className="space-y-3">
              {onStartNextWeek && (
                <Button
                  onClick={onStartNextWeek}
                  disabled={startingNextWeek}
                  className="w-full min-h-[48px] bg-amber-600 text-white hover:bg-amber-600/90 font-bold shadow-sm"
                >
                  <FaRedo className="mr-1.5" />
                  {startingNextWeek ? t("common.loading") : t("v2.home.startNextWeek")}
                </Button>
              )}
              {onCancelMatch && (
                <Button
                  type="button"
                  variant="outline"
                  className={cn("w-full min-h-[44px] font-semibold", destructiveOutlineBtnClass)}
                  disabled={cancellingMatch}
                  onClick={onCancelMatch}
                >
                  <FaTimes className="mr-1.5 opacity-70" />
                  {cancellingMatch ? t("common.loading") : t("v2.home.cancelMatch")}
                </Button>
              )}
            </div>
          ) : undefined
        }
        adminZone={
          isAdmin && (onStartNextWeek || onCancelMatch)
            ? {
                title: t("v2.home.adminZoneNextWeekTitle"),
                description: t("v2.home.adminZoneNextWeekDesc"),
                actionRequired: true,
              }
            : undefined
        }
      >
        <p className="text-sm text-muted-foreground">
          {isAdmin ? t("v2.home.staleSkippedDesc") : t("v2.home.adminOnlyStartWeek")}
        </p>
      </HomePhaseShell>
    );
  }

  if (mode === "completed_recap") {
    const completedSubtitle = isAdmin
      ? t("v2.home.completedCtaAdmin")
      : showLoopReset
        ? t("v2.home.completedCtaMember")
        : t("v2.home.completedDesc");

    return (
      <HomePhaseShell
        eyebrow={t("v2.home.phaseEyebrowCompleted")}
        title={t("v2.home.postMatchResultTitle")}
        meta={
          <span>
            {recapDateLabel ? `${recapDateLabel} · ` : ""}
            {completedSubtitle}
          </span>
        }
        secondary={
          <div className="space-y-4">
            {teaserLines.length > 0 && (
              <HomeSecondarySection label={t("v2.home.highlights")} defaultOpen>
                <ul className="space-y-1 text-sm">
                  {teaserLines.map((line, i) => (
                    <li key={i} className="font-medium">
                      {line}
                    </li>
                  ))}
                </ul>
              </HomeSecondarySection>
            )}
            {historySlot}
            {commentsSlot}
            <Link
              to="/v2/stats"
              className={cn(
                buttonVariants({ variant: "outline" }),
                "flex w-full min-h-[44px] items-center justify-between"
              )}
            >
              <span>{t("v2.home.goToStats")}</span>
              <FaChevronRight className="text-xs shrink-0" />
            </Link>
          </div>
        }
        adminSection={
          isAdmin && onStartNextWeek ? (
            <Button
              onClick={onStartNextWeek}
              disabled={startingNextWeek}
              className="w-full min-h-[48px] bg-amber-600 text-white hover:bg-amber-600/90 font-bold shadow-sm"
            >
              <FaRedo className="mr-1.5" />
              {startingNextWeek ? t("common.loading") : t("v2.home.startNextWeek")}
            </Button>
          ) : !isAdmin ? (
            <p className="text-sm text-muted-foreground text-center">
              {t("v2.home.adminOnlyStartWeek")}
            </p>
          ) : undefined
        }
        adminZone={
          isAdmin && onStartNextWeek
            ? {
                title: t("v2.home.adminZoneNextWeekTitle"),
                description: t("v2.home.adminZoneNextWeekDesc"),
                actionRequired: true,
              }
            : undefined
        }
      >
        <MatchResultScoreboard
          mode="display"
          light={displayScoreA}
          dark={displayScoreB}
          teamALabel={t("v2.home.teamA")}
          teamBLabel={t("v2.home.teamB")}
        />
        {winnerLabel && (
          <p className="flex items-center justify-center gap-1.5 text-sm font-semibold text-soccer-primary">
            <FaTrophy className="h-3.5 w-3.5" /> {winnerLabel}
          </p>
        )}
      </HomePhaseShell>
    );
  }

  const scoreboardMode = mode === "display" ? "display" : isAdmin ? "edit" : "pending";

  return (
    <HomePhaseShell
      eyebrow={t("v2.home.phaseEyebrowPostKickoff")}
      title={t("v2.home.postMatchResultTitle")}
      primaryAction={
        !isAdmin && scoreboardMode === "pending" && canDispute && onDispute ? (
          <Button variant="outline" onClick={onDispute} disabled={disputing} className="w-full min-h-[44px]">
            {disputing ? t("common.loading") : t("v2.home.disputeResult")}
          </Button>
        ) : undefined
      }
      secondary={
        commentsSlot || teamsSlot || historySlot ? (
          <div className="space-y-4">
            {commentsSlot}
            {teamsSlot && (
              <HomeSecondarySection label={t("v2.home.teamsPlayed")}>{teamsSlot}</HomeSecondarySection>
            )}
            {historySlot}
          </div>
        ) : undefined
      }
      adminSection={
        isAdmin ? (
          <div className="space-y-4">
            {showStaleBanner && onSkipStale && (
              <div className="rounded-xl border border-amber-400/60 bg-amber-400/10 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <FaExclamationTriangle className="text-amber-500 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="font-bold text-sm">{t("v2.home.staleResultTitle")}</p>
                    <p className="text-xs text-muted-foreground">{t("v2.home.staleResultDesc")}</p>
                  </div>
                </div>
                <Button onClick={onSkipStale} variant="outline" size="sm" className="w-full min-h-[44px]">
                  {t("v2.home.skipAndContinue")}
                </Button>
              </div>
            )}

            {scoreboardMode === "edit" && (
              <MatchResultAdminForm
                scoreA={scoreA}
                scoreB={scoreB}
                winner={winner}
                onScoreAChange={onScoreAChange}
                onScoreBChange={onScoreBChange}
                onWinnerChange={onWinnerChange}
                onSave={onLogResult}
                saveLabel={hasExistingResult ? t("v2.home.updateResult") : t("v2.home.saveResult")}
                saving={savingResult}
              />
            )}

            {onCancelMatch && (
              <Button
                type="button"
                variant="outline"
                className={cn("w-full min-h-[44px] font-semibold", destructiveOutlineBtnClass)}
                disabled={cancellingMatch}
                onClick={onCancelMatch}
              >
                <FaTimes className="mr-1.5 opacity-70" />
                {cancellingMatch ? t("common.loading") : t("v2.home.cancelMatch")}
              </Button>
            )}
          </div>
        ) : undefined
      }
      adminZone={
        isAdmin
          ? {
              title: t("v2.home.adminZoneResultTitle"),
              description: t("v2.home.adminZoneResultDesc"),
              actionRequired: scoreboardMode === "edit",
            }
          : undefined
      }
    >
      {scoreboardMode === "display" ? (
        <MatchResultScoreboard
          mode="display"
          light={displayScoreA}
          dark={displayScoreB}
          teamALabel={t("v2.home.teamA")}
          teamBLabel={t("v2.home.teamB")}
        />
      ) : (
        <div className="space-y-3">
          <MatchResultScoreboard mode="pending" teamALabel={t("v2.home.teamA")} teamBLabel={t("v2.home.teamB")} />
          <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <LuClock className="h-4 w-4 shrink-0" />
            {t("v2.home.awaitingOfficialResultDesc")}
          </p>
        </div>
      )}

      {mode === "display" && winnerLabel && (
        <p className="flex items-center justify-center gap-1.5 text-sm font-semibold text-soccer-primary">
          <FaTrophy className="h-3.5 w-3.5" /> {winnerLabel}
        </p>
      )}
    </HomePhaseShell>
  );
}
